import { ParsedModifierGroup } from '../types/menu';

/**
 * `_1..N` slot families among one level of an item's modifier groups — one
 * group per unit, such as a dozen's `bagel_choice_1..12` or a Crowd Pleaser's
 * `chili_dog_selection_1..4`.
 *
 * Shared by the repeated-slot panel (`findRepeatedSlotSets`) and the modifier
 * count rule (`getModifierMaxQuantity`), so both agree on what a slot is.
 */
export const SLOT_NAME_PATTERN = /^(.+)_(\d+)$/;

// `uniqueName` first: the front end's own min=max>1 split
// (menuUnification.utils.ts) writes `<constraint>_<n>` there and keeps the bare
// constraint name in `name`. Brand-menu constraints that are separate on the
// menu carry the suffix in `id` as well, so the fallback covers them.
export const getSlotName = (
  modGroup: Pick<ParsedModifierGroup, 'id' | 'uniqueName'>
) => modGroup.uniqueName || String(modGroup.id);

export interface SlotFamilyMember<G> {
  index: number;
  modGroup: G;
}

/**
 * Every well-formed family on the level, by stem: the indices run 1..N with no
 * gap or repeat. Any other `_<n>` name (a lone `combo_2`) is not a family.
 */
export const findSlotFamilies = <
  G extends Pick<ParsedModifierGroup, 'id' | 'uniqueName'>
>(
  modGroups: G[]
): Map<string, SlotFamilyMember<G>[]> => {
  const byStem = new Map<string, SlotFamilyMember<G>[]>();
  modGroups.forEach((modGroup) => {
    const match = SLOT_NAME_PATTERN.exec(getSlotName(modGroup));
    if (!match) return;
    const [, stem, index] = match;
    const members = byStem.get(stem) || [];
    members.push({ index: Number(index), modGroup });
    byStem.set(stem, members);
  });

  const families = new Map<string, SlotFamilyMember<G>[]>();
  byStem.forEach((members, stem) => {
    const sorted = [...members].sort((a, b) => a.index - b.index);
    if (sorted.every(({ index }, position) => index === position + 1)) {
      families.set(stem, sorted);
    }
  });
  return families;
};
